import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Client } from "../models/client.model";
import { cachedMasterDataService } from "../service/cachedMasterData.service";
import { clientService } from "../service/clientService";
import ClientSelectionList from "./ClientSelectionList";
import PageHeader from "./PageHeader";
import RightDrawer from "./RightDrawer";

const Clients: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { rmcId: rmcIdParam } = useParams<{ rmcId: string }>();
  const rmcIdState = location.state?.rmcId;
  const rmcNameState = location.state?.rmcName;
  const rmcId = rmcIdParam ? Number(rmcIdParam) : rmcIdState;
  const [rmcName, setRmcName] = useState<string | undefined>(rmcNameState);
  const consultantRmcId = useSelector((state: any) => state.auth.rmcId) ?? "";
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const [mappingGlobalFilter, setMappingGlobalFilter] = useState<string>("");
  const [mappingSearchInput, setMappingSearchInput] = useState<string>(""); // debounced input
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [showPopup, setShowPopup] = useState(false); // now controls right drawer visibility
  const [globalFilter, setGlobalFilter] = useState("");
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const effectiveConsultantRmcId =
    consultantRmcId && consultantRmcId !== 0
      ? consultantRmcId
      : Number(localStorage.getItem("consultantRmcId"));

  // Debounce search input for mapping
  useEffect(() => {
    const handle = setTimeout(() => {
      setMappingGlobalFilter(mappingSearchInput.trim());
    }, 320); // 320ms debounce
    return () => clearTimeout(handle);
  }, [mappingSearchInput]);

  useEffect(() => {
    fetchClients();
  }, [consultantRmcId, rmcId, userRole]);

  useEffect(() => {
    if (!rmcName && rmcId) {
      cachedMasterDataService.getRmcList().then((list) => {
        const found = (list || []).find(
          (r: any) => Number(r.id) === Number(rmcId)
        );
        if (found?.name) setRmcName(found.name);
      });
    }
  }, [rmcName, rmcId]);
  useEffect(() => {
    if (consultantRmcId && consultantRmcId !== 0) {
      localStorage.setItem("consultantRmcId", consultantRmcId.toString());
    }
  }, [consultantRmcId, userRole]);
  useEffect(() => {
    const loadClients = async () => {
      try {
        const [all, assigned] = await Promise.all([
          clientService.getAll(),
          clientService.getClientsByRmcId(rmcId),
        ]);
        setAllClients(all);
        const preSelectedIds = assigned
          .map((c: Client) => c.id)
          .filter((id): id is number => typeof id === "number");
        setSelectedClientIds(preSelectedIds);
        const preSelectedClients = all.filter((c) =>
          preSelectedIds.includes(c.id!)
        );
        setSelectedClients(preSelectedClients);
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    };
    if (showPopup) {
      loadClients();
    }
  }, [showPopup, rmcId]);

  const fetchClients = async () => {
    if (loadingClients) return; // Prevent duplicate calls
    setLoadingClients(true);
    try {
      let res;
      if (userRole === "Administrator") {
        if (!rmcId) {
          console.warn("RMC ID not found for Administrator role");
          setLoadingClients(false);
          return;
        }
        res = await clientService.getClientsByRmcId(rmcId);
      } else if (userRole === "RMCAdmin") {
        if (!effectiveConsultantRmcId) {
          console.warn("RMC ID not found for RMCAdmin role");
          setLoadingClients(false);
          return;
        }
        res = await clientService.getClientsByRmcId(effectiveConsultantRmcId);
      } else if (userRole === "RMCUser") {
        res = await clientService.getClientsForCurrentUser();
      } else {
        console.warn("Unsupported user role:", userRole);
        setLoadingClients(false);
        return;
      }
      setClients(res);
      setClientsLoaded(true);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };
  const handleSave = async () => {
    try {
      setClients([]);
      const effectiveRmcId = rmcId ?? effectiveConsultantRmcId;
      const parsedRmcId =
        typeof effectiveRmcId === "string"
          ? parseInt(effectiveRmcId, 10)
          : Number(effectiveRmcId);
      const clientIds = selectedClients
        .map((c) => c.id)
        .filter((id): id is number => typeof id === "number");
      if (clientIds.length === 0) {
        alert("Please select at least one client.");
        return;
      }
      const payload = { rmcId: parsedRmcId, clientIds: clientIds };
      const res = await clientService.saveRmcClientMappings(payload);
      if (res === true) {
        console.log("Saved Clients:", clientIds);
        fetchClients();
        setShowPopup(false);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Client mapped successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Client mapped failed.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };
  const onRowClick = (event: any) => {
    if (userRole === "RMCAdmin") return; // Disable click for RMC Admin
    const { data } = event;
    console.log("Row clicked:", data.rmcClientMappingId);
    if (data && data.id && data.name) {
      const target = rmcId
        ? `/rmc/${rmcId}/clients/${data.rmcClientMappingId}/proposal`
        : "/proposal";
      navigate(target, {
        state: {
          clientId: data.id,
          clientName: data.name,
          rmcClientMappingId: data.rmcClientMappingId,
          rmcId,
          cityId: data.cityId,
        },
      });
    }
  };
  const nameBodyTemplate = (rowData: Client) => (
    <span
      className={userRole !== "RMCAdmin" ? "text-primary text-link" : ""}
      style={{ cursor: userRole !== "RMCAdmin" ? "pointer" : "default" }}
      onClick={() => {
        if (userRole === "RMCAdmin") return; // Disable click for RMC Admin
        const target = rmcId
          ? `/rmc/${rmcId}/clients/${rowData.rmcClientMappingId}/proposal`
          : "/proposal";
        navigate(target, {
          state: {
            clientId: rowData.id,
            clientName: rowData.name,
            rmcClientMappingId: rowData.rmcClientMappingId,
            rmcId,
            cityId: rowData.cityId,
          },
        });
      }}
    >
      {rowData.name}
    </span>
  );

  return (
    <>
      <div className="content">
        <PageHeader
          showBackButton={userRole === "Administrator"}
          backTo="/rmc"
          title={rmcName ? `Client List - ${rmcName}` : "Client List"}
        />
        <div className="content-body">
          <div className="container-fluid mt-4">
            <div className="row shadow">
              <div className="col-md-12 mb-3">
                <DataTable
                  value={clients}
                  paginator
                  rows={5}
                  rowsPerPageOptions={[5, 10, 20]}
                  sortMode="multiple"
                  emptyMessage="No clients found."
                  dataKey="id"
                  globalFilter={globalFilter}
                  stripedRows
                  className="grid-table"
                  onRowClick={onRowClick}
                  header={
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <>
                        <IconField
                          iconPosition="left"
                          className="align-items-center d-flex"
                        >
                          <InputIcon>
                            <Icon.Search className="tx-12" />{" "}
                          </InputIcon>
                          <InputText
                            type="text"
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="Global Search"
                            style={{ minWidth: 250 }}
                          />
                        </IconField>
                        {globalFilter && (
                          <button
                            type="button"
                            className="btn btn-link p-0 ms-2"
                            onClick={() => setGlobalFilter("")}
                            aria-label="Clear search"
                            tabIndex={-1}
                          >
                            {" "}
                            &times;{" "}
                          </button>
                        )}
                      </>
                      {userRole === "Administrator" && (
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-primary"
                            onClick={() => setShowPopup(true)}
                          >
                            {" "}
                            <Icon.PlusCircle className="me-1" /> Assign Clients{" "}
                          </button>
                        </div>
                      )}
                    </div>
                  }
                >
                  <Column
                    field="name"
                    header="Client"
                    sortable
                    body={nameBodyTemplate}
                  />
                  <Column field="description" header="Description" sortable />
                  <Column field="address" header="Address" sortable />
                  <Column field="cityName" header="City Name" sortable />
                  <Column field="zipCode" header="Zip Code" sortable />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RightDrawer
        open={showPopup}
        title="Assign Clients"
        width={560}
        onClose={() => {
          setShowPopup(false);
          setSelectedClientIds([]);
        }}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowPopup(false);
                setSelectedClientIds([]);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                handleSave();
                setShowPopup(false);
              }}
            >
              Save
            </button>
          </>
        }
        headerExtra={
          <div className="drawer-search">
            <div className="drawer-search-field">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  background: "#ffffff",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                }}
              >
                <Icon.Search
                  style={{
                    marginRight: "8px",
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                />
                <input
                  type="text"
                  value={mappingSearchInput}
                  onChange={(e) => setMappingSearchInput(e.target.value)}
                  placeholder="Select Clients"
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    color: "#374151",
                  }}
                  aria-label="Search clients"
                />
                {mappingSearchInput && (
                  <button
                    type="button"
                    className="drawer-search-clear"
                    onClick={() => {
                      setMappingSearchInput("");
                      setMappingGlobalFilter("");
                    }}
                    aria-label="Clear search"
                    style={{
                      marginLeft: "8px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: "16px",
                      padding: "2px 4px",
                      borderRadius: "3px",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        }
      >
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <ClientSelectionList
            clients={allClients}
            selected={selectedClients}
            onChange={setSelectedClients}
            filter={mappingGlobalFilter}
            pagination={true}
            lazy={false}
            pageSizeOptions={[10, 20, 50, 100]}
            defaultPageSize={10}
            view="list"
          />
        </div>
      </RightDrawer>
    </>
  );
};
export default Clients;
